<svelte:options immutable />

<script lang="ts">
  import type { Image } from "bildebanken-model";
  import { getThumbnailUrl } from "state/thumbnails";
  import { getFileUploadStatus } from "state/upload";
  import { getBildebankenHost } from "../../config";
  import Progress from "../Common/Progress.svelte";
  import type { EditImageState } from "./editImageMachine";
  import { requiredFieldsMissingInImage } from "./editImageMachine";
  import { getKaleidoDerivateUrl } from "services/kaleido";
  import RightsIndicator from "../Common/RightsIndicator.svelte";
  import CreditPreview from "./CreditPreview.svelte";

  export let image: Partial<Image> & Pick<Image, "originalFileName">;
  export let selected: boolean;
  export let onSelect: () => void;
  export let onSelectMulti: () => void;
  export let state: EditImageState;

  $: missingFields = image.id ? requiredFieldsMissingInImage(state, image.id) : [];

  function onInputClick(event: MouseEvent) {
    if ((event.metaKey || event.ctrlKey) && onSelectMulti) {
      onSelectMulti();
    } else {
      onSelect();
    }
  }
  function checkboxChanged(event: MouseEvent) {
    event.stopPropagation();
    const target = event.target as HTMLInputElement;
    if (!target.checked && state.context.selectedIds.length === 2) {
      target.checked = true;
      onSelect();
    } else if (onSelectMulti) {
      onSelectMulti();
    }
  }

  const filename = image.originalFileName;

  // we use filename for lookup because not all files have an ID
  const uploadStatus = getFileUploadStatus(filename);

  function getUploadError() {
    if ($uploadStatus?.state === "FAILED" && "error" in $uploadStatus) {
      return $uploadStatus.error || "Ukjent årsak";
    }
  }

  let err = false;

  const getEmptyImageUrl = () => {
    return new URL("/image-icon.svg", getBildebankenHost()).toString();
  };

  async function getThumbnailOrKaleidoDerivateUrl() {
    const thumbnailUrl = getThumbnailUrl(image);

    // Fallback to Kaleido derivate if thumbnail is missing
    if (!thumbnailUrl && image.metadata?.publicId) {
      return await getKaleidoDerivateUrl(image.metadata?.publicId, { width: 300, height: 300 });
    }
    return thumbnailUrl;
  }

  const aspectRatioPlaceholder = 270 / 160;
  const aspectRatioImage =
    image?.technicalMetadata?.technical_image_width &&
    image?.technicalMetadata?.technical_image_height
      ? image.technicalMetadata.technical_image_width /
        image?.technicalMetadata?.technical_image_height
      : undefined;

  const rightsLabelMap = {
    Unknown: "Ukjent",
    Free: "Fri bruk",
    Restricted: "Begrenset",
  } as const;

  function isValidRightFields(image: Partial<Image>) {
    const rightsMarker = image.metadata?.rightsMarker;
    return (
      (rightsMarker === "Restricted" &&
        (!!image.metadata?.rights?.note || !!image.metadata?.instructions)) ||
      (rightsMarker === "Free" && image.metadata?.rights?.rightsHolder) ||
      rightsMarker === "Unknown"
    );
  }
</script>

<button
  class="tile"
  class:failed={$uploadStatus?.state === "FAILED" || missingFields.length > 0}
  class:selected
  data-testid="edit-preview"
  on:click={onInputClick}
  aria-label="Velg bilde: {image.metadata?.title || image.originalFileName}"
>
  <div class="left-half">
    {#if err}
      <img class="error" alt="" src={getEmptyImageUrl()} />
    {:else}
      {#await getThumbnailOrKaleidoDerivateUrl() then thumbnail}
        <img
          style={aspectRatioImage && aspectRatioImage > aspectRatioPlaceholder
            ? "width: 100%"
            : "height:100%"}
          alt=""
          src={thumbnail}
          on:error={() => (err = true)}
        />
      {/await}
    {/if}
  </div>
  <div class="right-half">
    <div class="tile-description">
      {#if $uploadStatus?.state === "FAILED"}
        <div class="selectable-text">
          <h3>
            {filename}
          </h3>
          <div class="org-warning">
            Opplasting feilet: {getUploadError()}
          </div>
        </div>
      {:else}
        {#if state.matches("Editing.Single")}
          <label for="image-title-{image.id}">
            <div id="image-title-{image.id}" class="selectable-text">{image.metadata?.title}</div>
          </label>
        {:else}
          <strong>{image.metadata?.title}</strong>
        {/if}
        <div class="selectable-text">{image.metadata?.description || ""}</div>
      {/if}
    </div>
    <div class="rights-indicator">
      {rightsLabelMap[image.metadata?.rightsMarker || "Unknown"]}
      <RightsIndicator rightsMarker={image.metadata?.rightsMarker || "Unknown"} />
    </div>
    <CreditPreview {image} />
    {#if missingFields?.length > 0 || (image && !isValidRightFields(image))}
      <div
        class="org-warning"
        style="padding-top: var(--org-small); padding-bottom: var(--org-small)"
      >
        Obligatoriske felt mangler
      </div>
    {/if}
    {#if $uploadStatus}
      <Progress
        status={$uploadStatus.state === "FAILED"
          ? "FAILED"
          : $uploadStatus.state === "SUCCESS"
          ? "SUCCESS"
          : "IN_PROGRESS"}
        value={$uploadStatus.progress}
        id={"file-upload-" + filename}
        srLabel={"Opplasting"}
      />
    {/if}
    {#if state.context.images.length > 1}
      <input
        type="checkbox"
        checked={selected}
        class="org-input"
        aria-label="Velg bilde"
        disabled={state.context.selectedIds.length === 1 &&
          state.context.selectedIds[0] === image.id}
        on:click|stopPropagation={checkboxChanged}
      />
    {/if}
  </div>
</button>

<style>
  .tile {
    /* Reset button default styles */
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    text-align: inherit;

    /* Original tile styles */
    display: flex;
    flex-shrink: 0;
    border: solid 2px transparent;
    border-radius: var(--bb-standard-border-radius);
    transition: border 200ms;
    overflow: hidden;
    position: relative;
    color: var(--color-text-invert);
  }

  .tile:hover {
    border-color: var(--org-color-gray-500);
    cursor: pointer;
  }

  .tile.selected:has(:focus),
  .tile.selected:has(input:disabled) {
    border-color: var(--org-color-focus);
  }

  input[type="checkbox"] {
    cursor: pointer;
    margin: var(--org-xsmall);
    margin-left: var(--org-small);
    position: absolute;
    right: var(--org-small);
    top: var(--org-small);
    /* pointer-events: none; */
  }

  .selected,
  .tile.selected:hover {
    border-color: var(--org-color-primary);
  }

  .selected .right-half {
    background-color: var(--bb-selected-bg-color);
  }

  .failed,
  .tile.failed:hover {
    border-color: var(--org-color-warning);
    background-color: var(--color-surface-primary);
    cursor: unset;
    filter: none;
  }

  .right-half {
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: var(--org-small);
    gap: var(--org-small);
    height: 100%;
    position: relative;
    background: var(--color-surface-secondary);
  }

  .left-half {
    width: 270px;
    height: 160px;
    object-fit: contain;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .left-half img.error {
    width: max(20px, calc(40px * var(--scale)));
    height: max(20px, calc(40px * var(--scale)));
  }

  .tile-description {
    color: var(--color-text-primary);
    flex: 1;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    line-clamp: 4;
    -webkit-line-clamp: 4;
    overflow: hidden;
    transition:
      background-color 200ms,
      filter 200ms;
  }

  .selectable-text {
    user-select: text;
  }

  .tile:hover .tile-description {
    filter: brightness(1.1);
  }

  .org-warning {
    width: fit-content;
    padding-right: 1em;
  }

  .rights-indicator {
    color: var(--color-text-primary);
  }
</style>
